import dayjs from "dayjs";

/**
 * The menu day to request, as a Date at LOCAL midnight. `new Date("2026-10-07")`
 * is midnight UTC, which on a phone set behind UTC (the Americas) is still the
 * 6th: the app asked for the wrong day, stored it as fetched, never matched the
 * day on screen, and refetched until the member's API allowance ran out —
 * Home, coins and feed then failed too (07.10.2026).
 */
export const menuRequestDate = (day) => dayjs(day).startOf("day").toDate();

/**
 * Italian day names indexed 1..7 where 1 = Lunedì and 7 = Domenica.
 * Matches the `unlocks.menu_days` numeric convention used in the percorso API.
 */
export const DAY_NAMES_IT = {
  1: "Lunedì",
  2: "Martedì",
  3: "Mercoledì",
  4: "Giovedì",
  5: "Venerdì",
  6: "Sabato",
  7: "Domenica",
};

/**
 * Resolve the Monday of the user's active menu week, in YYYY-MM-DD form.
 *
 * Order of preference (first non-empty wins):
 *   1. subscription.free_route.start — the canonical Monday of the active
 *      free-route week. This is the source the Menu page itself uses for
 *      `activeWeekRange`, so callers that rely on this stay aligned with
 *      the Menu page's calendar regardless of payment state.
 *   2. subscription.start_date — paid-subscription start. Used for premium
 *      users who don't have a free_route attached.
 *   3. trialInfoRouteStart — legacy fallback for callers that already have
 *      this in scope (e.g. existing `getTrialInfo()` consumers). Kept so the
 *      migration to this helper can be incremental.
 *
 * Returns null if every candidate is missing/invalid; the caller decides
 * what to do (typically: don't render a deep link).
 */
export const resolveActiveWeekStart = ({
  subscription,
  trialInfoRouteStart,
} = {}) => {
  const candidates = [
    subscription?.free_route?.start,
    subscription?.start_date,
    trialInfoRouteStart,
  ];
  for (const v of candidates) {
    if (!v) continue;
    const dateOnly = String(v).includes("T") ? String(v).split("T")[0] : v;
    if (dayjs(dateOnly, "YYYY-MM-DD", true).isValid()) return dateOnly;
  }
  return null;
};

/**
 * Given a 1..7 weekday and a Monday ISO date, return the calendar ISO date
 * for that weekday in the same week. Returns null if either input is invalid.
 *
 * Example: weekdayIsoFromMonday(6, "2026-05-11") -> "2026-05-16" (Saturday).
 */
export const weekdayIsoFromMonday = (weekday1to7, mondayIso) => {
  const n = Number(weekday1to7);
  if (!Number.isInteger(n) || n < 1 || n > 7) return null;
  if (!mondayIso) return null;
  const monday = dayjs(mondayIso);
  if (!monday.isValid()) return null;
  return monday.add(n - 1, "day").format("YYYY-MM-DD");
};

/**
 * Where the bottom-bar Menu takes a FREE member: today when it falls inside
 * their program week, otherwise the program's first day (the preparation week
 * and anything after it). Null when the program start is unknown, so the caller
 * keeps the plain /menu.
 */
export const freeProgramMenuPath = ({
  subscription,
  trialInfoRouteStart,
  today = dayjs(),
} = {}) => {
  const weekStart = resolveActiveWeekStart({ subscription, trialInfoRouteStart });
  if (!weekStart) return null;

  const start = dayjs(weekStart);
  const endSource = subscription?.free_route?.end ?? subscription?.end_date;
  const end = endSource
    ? dayjs(String(endSource).split("T")[0].split(" ")[0])
    : start.add(6, "day");
  const day = dayjs(today).startOf("day");

  const insideProgram =
    !day.isBefore(start, "day") && !day.isAfter(end, "day");
  return `/menu/${(insideProgram ? day : start).format("YYYY-MM-DD")}`;
};
